import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  RefreshCw, Wallet, CheckCircle2, Send, Search, CheckSquare, Square, FileDown,
} from "lucide-react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import usePagedRows from "../components/usePagedRows";
import Pagination from "../components/Pagination";

const ACCENT = "#22c073";
const GOLD = "#d4af37";
const NAVY = "#0a1018";

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AffiliateSettlements() {
  const [tab, setTab] = useState("pending"); // pending|credited|paid|all
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkRef, setBulkRef] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [players, setPlayers] = useState([]);
  const [pLoading, setPLoading] = useState(false);

  useEffect(() => {
    if (!detailId) { setPlayers([]); return undefined; }
    let cancelled = false;
    setPLoading(true);
    api.get(`/admin/aff-settlements/${detailId}/players`)
      .then((r) => { if (!cancelled) setPlayers(r.data?.items || []); })
      .catch((e) => { if (!cancelled) toast.error(e?.response?.data?.detail || "Failed"); })
      .finally(() => { if (!cancelled) setPLoading(false); });
    return () => { cancelled = true; };
  }, [detailId]);

  const downloadPdf = async (id) => {
    try {
      const token = localStorage.getItem("admin_token") || localStorage.getItem("ss_admin_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/aff-settlements/${id}.pdf`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("PDF download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `settlement_${id.slice(0, 8)}.pdf`;
      a.click();
    } catch (e) { toast.error("Could not download PDF"); }
  };

  const filtered = rows.filter((r) => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return (r.affiliate_username || "").toLowerCase().includes(s)
        || (r.promo_code || "").toLowerCase().includes(s);
  });
  const paged = usePagedRows(filtered, 15, [q, tab]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/aff-settlements", { params: { status: tab, days: 365 } });
      setRows(data.rows || []);
      setCounts(data.counts || {});
      setSelected(new Set());
    } catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const toggleSel = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.filter(r => r.status !== "paid").map(r => r.id)));
  };

  const markPaid = async (id) => {
    const ref = window.prompt("Transaction reference (optional):", "") ?? "";
    try {
      await api.post(`/admin/aff-settlements/${id}/mark-paid`, { tx_ref: ref });
      toast.success("Marked as paid");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const bulkMarkPaid = async () => {
    if (selected.size === 0) return toast.error("Select at least one record");
    if (!window.confirm(`Mark ${selected.size} commission records as PAID? This is a batch settlement and cannot be undone.`)) return;
    try {
      const { data } = await api.post("/admin/aff-settlements/bulk-mark-paid", {
        commission_ids: Array.from(selected),
        tx_ref: bulkRef || "",
      });
      toast.success(`Settled ${data.processed} records`);
      setBulkRef("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const totals = {
    pending: counts.pending || { count: 0, total: 0 },
    below_threshold: counts.below_threshold || { count: 0, total: 0 },
    credited: counts.credited || { count: 0, total: 0 },
    paid: counts.paid || { count: 0, total: 0 },
  };

  return (
    <div>
      <PageHeader title="Commission Settlements" subtitle="Manual settlement of affiliate commissions — 15-day cycle · pending → credited → paid · batch supported" />

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Stat label="Pending" value={`৳ ${fmt(totals.pending.total)}`} sub={`${totals.pending.count} records`} c={GOLD} />
        <Stat label="Credited to Wallet" value={`৳ ${fmt(totals.credited.total)}`} sub={`${totals.credited.count} records`} c={ACCENT} />
        <Stat label="Paid" value={`৳ ${fmt(totals.paid.total)}`} sub={`${totals.paid.count} records`} c="#3b82f6" />
        <Stat label="Below Threshold" value={`৳ ${fmt(totals.below_threshold.total)}`} sub={`${totals.below_threshold.count} records`} c="#f59e0b" />
      </div>

      {/* Tabs + actions */}
      <div className="rounded-xl p-3 mb-3 flex items-center gap-2 flex-wrap" style={{ background: `linear-gradient(180deg, ${NAVY}d9, ${NAVY}f5)`, border: `1px solid ${ACCENT}3a` }}>
        {["all", "pending", "credited", "paid", "below_threshold"].map((t) => (
          <button key={t} onClick={() => setTab(t)} className="h-9 px-3 rounded-md text-[12px] font-bold inline-flex items-center gap-1 capitalize" style={{
            background: tab === t ? `${ACCENT}28` : "rgba(255,255,255,0.04)",
            border: `1px solid ${tab === t ? ACCENT : "rgba(255,255,255,0.10)"}`,
            color: tab === t ? ACCENT : "#cbd5e1",
          }} data-testid={`settle-tab-${t}`}>{t.replace("_", " ")}</button>
        ))}
        <div className="flex items-center gap-2 h-9 px-3 rounded-md flex-1 max-w-md ml-2" style={{ background: "rgba(0,0,0,0.32)", border: `1px solid ${ACCENT}30` }}>
          <Search size={13} style={{ color: ACCENT }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="affiliate username / promo code" className="bg-transparent outline-none text-[13px] flex-1 text-white" />
        </div>
        <button onClick={load} className="h-9 px-3 rounded-md text-[12px] inline-flex items-center gap-1.5 font-semibold" style={{ background: `${ACCENT}1a`, border: `1px solid ${ACCENT}55`, color: ACCENT }}><RefreshCw size={12} /> Refresh</button>
        {selected.size > 0 && (
          <>
            <input value={bulkRef} onChange={(e) => setBulkRef(e.target.value)} placeholder="Batch tx-ref (optional)" className="h-9 px-3 rounded-md text-[12px] outline-none" style={{ background: "rgba(0,0,0,0.32)", border: `1px solid ${GOLD}40`, color: "#fff", width: 200 }} data-testid="bulk-tx-ref" />
            <button onClick={bulkMarkPaid} className="h-9 px-3 rounded-md text-[12px] inline-flex items-center gap-1.5 font-bold" style={{ background: `${ACCENT}28`, border: `1px solid ${ACCENT}77`, color: ACCENT }} data-testid="bulk-mark-paid"><Send size={12} /> Bulk Mark Paid ({selected.size})</button>
          </>
        )}
      </div>

      <div className="rounded-xl p-3 overflow-x-auto text-white" style={{ background: `linear-gradient(180deg, ${NAVY}d9, ${NAVY}f5)`, border: `1px solid ${ACCENT}3a` }}>
        {loading ? <div className="text-white/55 py-10 text-center">Loading…</div>
          : filtered.length === 0 ? <div className="text-white/55 py-10 text-center">No settlement records found</div>
          : (
            <table className="w-full text-sm min-w-[900px]" data-testid="settlements-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest" style={{ color: `${ACCENT}cc` }}>
                  <th className="py-2 pr-2 w-7"><button onClick={toggleAll} style={{ color: ACCENT }}>{selected.size === filtered.filter(r=>r.status!=="paid").length && filtered.length > 0 ? <CheckSquare size={14} /> : <Square size={14} />}</button></th>
                  <th className="py-2 pr-2">Affiliate</th>
                  <th className="py-2 pr-2">Promo Code</th>
                  <th className="py-2 pr-2">Period</th>
                  <th className="py-2 pr-2 text-right">Net Loss ৳</th>
                  <th className="py-2 pr-2 text-right">Rate %</th>
                  <th className="py-2 pr-2 text-right">Commission ৳</th>
                  <th className="py-2 pr-2">Status</th>
                  <th className="py-2 pr-2">Tx Ref</th>
                  <th className="py-2 pr-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {paged.visible.map((r) => (
                  <tr key={r.id} className="hover:bg-emerald-500/5" style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                    <td className="py-2.5 pr-2">
                      {r.status !== "paid" && (
                        <button onClick={() => toggleSel(r.id)} style={{ color: ACCENT }} data-testid={`sel-${r.id}`}>
                          {selected.has(r.id) ? <CheckSquare size={14} /> : <Square size={14} />}
                        </button>
                      )}
                    </td>
                    <td className="py-2.5 pr-2 font-bold">{r.affiliate_username || "—"}</td>
                    <td className="py-2.5 pr-2 font-mono" style={{ color: GOLD }}>{r.promo_code || "—"}</td>
                    <td className="py-2.5 pr-2 text-xs text-white/70">{(r.period_start || "").slice(0, 10)} → {(r.period_end || "").slice(0, 10)}</td>
                    <td className="py-2.5 pr-2 text-right">৳ {fmt(r.net_loss)}</td>
                    <td className="py-2.5 pr-2 text-right">{Number(r.commission_rate || 0).toFixed(0)}%</td>
                    <td className="py-2.5 pr-2 text-right font-black" style={{ color: ACCENT }}>৳ {fmt(r.commission_amount)}</td>
                    <td className="py-2.5 pr-2">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest" style={{
                        background: r.status === "paid" ? "rgba(59,130,246,0.18)" : r.status === "credited" ? "rgba(34,192,115,0.18)" : "rgba(212,175,55,0.18)",
                        border: r.status === "paid" ? "1px solid rgba(59,130,246,0.45)" : r.status === "credited" ? "1px solid rgba(34,192,115,0.5)" : "1px solid rgba(212,175,55,0.45)",
                        color: r.status === "paid" ? "#93c5fd" : r.status === "credited" ? "#6ee7b7" : "#fff7d6",
                      }}>{(r.status || "pending").replace("_", " ")}</span>
                    </td>
                    <td className="py-2.5 pr-2 text-xs text-white/70 font-mono">{(r.tx_ref || "—").slice(0, 24)}</td>
                    <td className="py-2.5 pr-2">
                      <div className="inline-flex gap-1">
                        {r.status !== "paid" && (
                          <button onClick={() => markPaid(r.id)} className="px-2 h-7 rounded-md text-xs font-bold inline-flex items-center gap-1" style={{ background: `${ACCENT}28`, border: `1px solid ${ACCENT}55`, color: ACCENT }} data-testid={`mark-paid-${r.id}`}>
                            <CheckCircle2 size={11} /> Mark Paid
                          </button>
                        )}
                        <button onClick={() => setDetailId(r.id)} className="px-2 h-7 rounded-md text-xs font-bold inline-flex items-center gap-1" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "#cbd5e1" }} data-testid={`players-${r.id}`}>
                          Players
                        </button>
                        <button onClick={() => downloadPdf(r.id)} className="px-2 h-7 rounded-md text-xs font-bold inline-flex items-center gap-1" style={{ background: `${GOLD}28`, border: `1px solid ${GOLD}55`, color: GOLD }} data-testid={`pdf-${r.id}`}>
                          <FileDown size={11} /> PDF
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        <Pagination total={paged.totalRows} page={paged.page} pageSize={paged.pageSize} onChange={paged.setPage} testIdPrefix="settle-pg" />
      </div>

      {detailId && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.75)" }} onClick={() => setDetailId(null)}>
          <div className="rounded-xl bg-[#0f1a2e] border border-emerald-500/30 max-w-3xl w-full max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-bold">PLAYER BREAKDOWN</div>
                <div className="text-white font-black text-lg">Settlement {detailId.slice(0, 8)}</div>
              </div>
              <button onClick={() => downloadPdf(detailId)} className="px-3 h-9 rounded-lg inline-flex items-center gap-1 text-[12px] font-bold" style={{ background: `${GOLD}28`, border: `1px solid ${GOLD}55`, color: GOLD }} data-testid="modal-pdf">
                <FileDown size={12} /> Download PDF
              </button>
            </div>
            <div className="rounded-lg overflow-hidden border border-white/10">
              <table className="w-full text-[12.5px]">
                <thead><tr className="bg-white/[0.04] text-emerald-300">
                  <th className="text-left p-2.5">Username</th>
                  <th className="text-right p-2.5">Deposit</th>
                  <th className="text-right p-2.5">Withdraw</th>
                  <th className="text-right p-2.5">Net Loss</th>
                  <th className="text-right p-2.5">Commission</th>
                </tr></thead>
                <tbody>
                  {pLoading ? (
                    <tr><td colSpan={5} className="p-6 text-center text-white/55">Loading…</td></tr>
                  ) : players.length === 0 ? (
                    <tr><td colSpan={5} className="p-6 text-center text-white/55">No player activity</td></tr>
                  ) : players.map((p, i) => (
                    <tr key={i} className="border-t border-white/5">
                      <td className="p-2.5 text-white">{p.username || "—"}</td>
                      <td className="p-2.5 text-right text-white/85">৳{fmt(p.deposit)}</td>
                      <td className="p-2.5 text-right text-white/85">৳{fmt(p.withdraw)}</td>
                      <td className="p-2.5 text-right font-bold" style={{ color: p.net_loss >= 0 ? "#a7f3d0" : "#fca5a5" }}>৳{fmt(p.net_loss)}</td>
                      <td className="p-2.5 text-right font-bold" style={{ color: GOLD }}>৳{fmt(p.commission_contribution)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub, c }) {
  return (
    <div className="rounded-xl p-4" style={{
      background: `radial-gradient(400px 120px at 0% 0%, ${c}18, transparent 60%), linear-gradient(180deg, ${NAVY}d9, ${NAVY}f5)`,
      border: `1px solid ${c}3a`,
    }}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/55 font-bold">{label}</div>
        <Wallet size={14} style={{ color: c }} />
      </div>
      <div className="text-[20px] font-black" style={{ color: c }}>{value}</div>
      {sub && <div className="text-[10.5px] text-white/45 mt-0.5">{sub}</div>}
    </div>
  );
}
